/**
 * Transient confirmations, one queue for the whole app.
 *
 * Every write in this app used to end silently: the modal closed, the list
 * refreshed, and whether the save landed was something you inferred from the
 * row appearing. That is fine when it works and awful when it does not — a
 * 403 looked exactly like success. So a write says what it did, in the
 * domain's own words, and an error stays twice as long as a success because
 * you have to read it before you can act on it.
 *
 * Deliberately not a queue with animation states: the value is the sentence,
 * not the transition.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

type Tone = "success" | "error" | "info";
type Entry = { id: number; tone: Tone; text: string };

type ToastApi = {
  success: (text: string) => void;
  error: (text: string) => void;
  info: (text: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const LIFETIME: Record<Tone, number> = { success: 3500, info: 3500, error: 7000 };

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const nextId = useRef(1);

  const push = useCallback((tone: Tone, text: string) => {
    const id = nextId.current++;
    setEntries((current) => [...current, { id, tone, text }]);
    window.setTimeout(
      () => setEntries((current) => current.filter((entry) => entry.id !== id)),
      LIFETIME[tone],
    );
  }, []);

  // Stable identity: an unstable api object re-renders every consumer on every
  // toast, and pages hold it in effect dependency lists.
  const api = useMemo<ToastApi>(() => ({
    success: (text) => push("success", text),
    error: (text) => push("error", text),
    info: (text) => push("info", text),
  }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-4 z-[990] flex flex-col items-center gap-2 px-4"
      >
        {entries.map((entry) => (
          <div
            key={entry.id}
            role={entry.tone === "error" ? "alert" : undefined}
            className={`pointer-events-auto max-w-lg rounded-lg px-4 py-2.5 text-sm font-medium shadow-lg ${
              entry.tone === "error"
                ? "bg-danger text-white"
                : entry.tone === "success"
                  ? "bg-success text-white"
                  : "bg-chrome text-white"
            }`}
          >
            {entry.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Never throws when the provider is absent: a missing toast must not take a
 * screen down with it, and a unit test rendering one component in isolation
 * should not have to mount the whole shell.
 */
const NOOP: ToastApi = { success: () => {}, error: () => {}, info: () => {} };

export function useToast(): ToastApi {
  return useContext(ToastContext) ?? NOOP;
}
